import { useEffect, useRef } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router'
import { TopBar, AppLayout, Mtds, useAuth, useSystemLog } from '../index'

const loggedMessages = new Set<string>()

type ErrorScreenProps = {
  logo: React.ReactNode
  useMobileBar?: boolean
  defaultErrorTitle?: string
  defaultErrorMessage?: string
  error?: unknown
  retryLogin?: () => void
}

export const ErrorScreen = ({
  logo,
  useMobileBar,
  defaultErrorTitle = 'Feil',
  defaultErrorMessage = 'Noe gikk galt',
  error: suppliedError,
  retryLogin
}: ErrorScreenProps) => {
  const routeError = useRouteError()
  const error = suppliedError ?? routeError
  const auth = useAuth()
  const systemLog = useSystemLog()
  const loggedMessageRef = useRef<string | undefined>(undefined)
  let errorText = defaultErrorTitle
  let errorData = defaultErrorMessage

  if (isRouteErrorResponse(error)) {
    errorText = `${error.status} ${error.statusText}`
    errorData = error.data
  } else if (error instanceof Error) {
    errorText = error.name
    errorData = error.message
  } else {
    console.error('Unknown error', error)
  }
  const errorDetails = `${errorText}: ${String(errorData)}`
  useEffect(() => {
    if (!auth.isAuthenticated) return

    const logMessage = `Error: ${errorText} - Details: ${errorData} - Location: ${globalThis.location.href} - User agent: ${globalThis.navigator.userAgent} - User id: ${auth.userId}`
    if (
      loggedMessageRef.current === logMessage ||
      loggedMessages.has(logMessage)
    ) {
      return
    }
    loggedMessageRef.current = logMessage
    loggedMessages.add(logMessage)
    if (loggedMessages.size > 200) loggedMessages.clear()

    systemLog.mutate({
      type: 'ERROR',
      message: logMessage,
      timestamp: Date.now().toString()
    })
  }, [auth.isAuthenticated, auth.userId, errorData, errorText, systemLog])

  return (
    <AppLayout
      topBar={<TopBar logo={logo} />}
      useMobileBar={useMobileBar}
      isError={true}
      error={new Error(errorDetails)}
      errorActionButton={
        retryLogin ? (
          <Mtds.Button data-variant='primary' onClick={retryLogin}>
            Prøv innlogging på nytt
          </Mtds.Button>
        ) : undefined
      }
    />
  )
}
